// OrbShatter.js — Three-phase orb shattering animation
// Phase 1 (0-400ms):   Fracture lines appear, orb holds together
// Phase 2 (400-1200ms): Shards fly outward, rotate, fade
// Phase 3 (1200-3000ms): Aftermath — luminous ring fades, silence
// Phase 4 (2000ms+):   onComplete called → DaemonTag rises (handled by parent)

function hexRgb(hex) {
  if (!hex || hex[0] !== '#') return '112,64,208';
  return `${parseInt(hex.slice(1,3),16)},${parseInt(hex.slice(3,5),16)},${parseInt(hex.slice(5,7),16)}`;
}

// Generate triangular shards that together tile the circle
function generateShards(cx, cy, r) {
  const count = 14;

  // Perimeter points with angular jitter
  const perims = Array.from({ length: count }, (_, i) => {
    const base   = (i / count) * Math.PI * 2;
    const jitter = (Math.random() - 0.5) * (Math.PI / count);
    const ang    = base + jitter;
    const rad    = r * (0.91 + Math.random() * 0.09);
    return { x: cx + Math.cos(ang) * rad, y: cy + Math.sin(ang) * rad, ang };
  });

  // A handful of interior focal points
  const foci = Array.from({ length: 5 }, () => {
    const a = Math.random() * Math.PI * 2;
    const d = r * (0.08 + Math.random() * 0.48);
    return { x: cx + Math.cos(a) * d, y: cy + Math.sin(a) * d };
  });

  return perims.map((p0, i) => {
    const p1 = perims[(i + 1) % count];
    const midAng = (p0.ang + p1.ang) / 2;
    const mx = cx + Math.cos(midAng) * r * 0.38;
    const my = cy + Math.sin(midAng) * r * 0.38;

    // Choose the nearest focal point to this edge's midpoint
    let apex = { x: cx, y: cy };
    let minD = Math.hypot(mx - cx, my - cy);
    foci.forEach(f => {
      const d = Math.hypot(mx - f.x, my - f.y);
      if (d < minD) { minD = d; apex = f; }
    });

    const centX = (apex.x + p0.x + p1.x) / 3;
    const centY = (apex.y + p0.y + p1.y) / 3;

    return {
      poly: [apex, p0, p1],
      centX, centY,
      angle:    Math.atan2(centY - cy, centX - cx),
      velocity: 200 + Math.random() * 200,   // px over 800ms
      spinRad:  (Math.random() - 0.5) * 0.7, // total rotation in radians
    };
  });
}

export function startShatter(bottomCanvas, topCanvas, wrapper, cx, cy, r, material, onComplete) {
  const matColor = material?.primary || '#7040d0';
  const matRgb   = hexRgb(matColor);
  const diam     = cx * 2;

  // ── Create composite snapshot at logical size ──────────────────────────────
  const snap = document.createElement('canvas');
  snap.width  = diam;
  snap.height = diam;
  const sx = snap.getContext('2d');
  sx.drawImage(bottomCanvas, 0, 0, diam, diam);
  sx.drawImage(topCanvas,    0, 0, diam, diam);

  const shards = generateShards(cx, cy, r);

  // ── Shatter canvas sits over everything ───────────────────────────────────
  const sc = document.createElement('canvas');
  sc.width  = diam;
  sc.height = diam;
  Object.assign(sc.style, {
    position: 'absolute', inset: '0',
    width: '100%', height: '100%',
    pointerEvents: 'none', zIndex: '50',
  });
  wrapper.appendChild(sc);
  const ctx = sc.getContext('2d');

  let start = null;

  function frame(ts) {
    if (!start) start = ts;
    const elapsed = ts - start;
    ctx.clearRect(0, 0, diam, diam);

    if (elapsed < 400) {
      // ── Phase 1: Intact orb + crack lines appearing ────────────────────
      const t = elapsed / 400;

      // Draw orb from snapshot
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(snap, 0, 0, diam, diam);
      ctx.restore();

      // Draw emerging fracture lines
      ctx.save();
      ctx.globalAlpha = t * 0.9;
      ctx.strokeStyle = `rgba(${matRgb}, ${0.9})`;
      ctx.lineWidth   = 0.6;
      shards.forEach(s => {
        ctx.beginPath();
        ctx.moveTo(s.poly[0].x, s.poly[0].y);
        s.poly.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.stroke();
      });
      // Bright flash at crack lines
      ctx.strokeStyle = `rgba(255,255,255,${t * 0.35})`;
      ctx.lineWidth   = 0.3;
      shards.forEach(s => {
        ctx.beginPath();
        ctx.moveTo(s.poly[0].x, s.poly[0].y);
        s.poly.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.stroke();
      });
      ctx.restore();

    } else if (elapsed < 1200) {
      // ── Phase 2: Shards flying ─────────────────────────────────────────
      const rawT  = (elapsed - 400) / 800;
      const eased = 1 - Math.pow(1 - rawT, 2.5); // ease-out

      shards.forEach(s => {
        const tx   = Math.cos(s.angle) * s.velocity * eased;
        const ty   = Math.sin(s.angle) * s.velocity * eased;
        const spin = s.spinRad * eased;
        const op   = 1 - rawT;

        ctx.save();
        ctx.globalAlpha = op;
        ctx.translate(s.centX + tx, s.centY + ty);
        ctx.rotate(spin);
        ctx.translate(-s.centX, -s.centY);

        // Clip to shard polygon, draw orb texture inside
        ctx.beginPath();
        ctx.moveTo(s.poly[0].x, s.poly[0].y);
        s.poly.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(snap, 0, 0, diam, diam);

        // Bright edge — catching the light
        ctx.strokeStyle = `rgba(${matRgb}, ${op * 0.85})`;
        ctx.lineWidth   = 1.5;
        ctx.stroke();
        const edgeGlow = ctx.createLinearGradient(s.poly[0].x, s.poly[0].y, s.centX, s.centY);
        edgeGlow.addColorStop(0, `rgba(255,255,255,${op * 0.3})`);
        edgeGlow.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = edgeGlow;
        ctx.fill();

        ctx.restore();
      });

    } else if (elapsed < 3000) {
      // ── Phase 3: Aftermath — ring and ambient glow fading ──────────────
      const rawT   = (elapsed - 1200) / 1800;
      const ringOp = Math.max(0, 1 - rawT);

      // Ground shadow (fades faster)
      ctx.save();
      ctx.globalAlpha = Math.max(0, 1 - rawT * 1.5) * 0.25;
      const shadow = ctx.createRadialGradient(cx, cy + r * 0.9, 0, cx, cy + r * 0.9, r * 0.65);
      shadow.addColorStop(0, 'rgba(0,0,0,0.8)');
      shadow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = shadow;
      ctx.fillRect(cx - r, cy + r * 0.25, r * 2, r * 1.5);
      ctx.restore();

      // Luminous ring afterimage
      ctx.save();
      ctx.globalAlpha = ringOp * 0.6;
      const ringR = r * (1 + rawT * 0.08);
      ctx.beginPath();
      ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${matRgb}, ${ringOp})`;
      ctx.lineWidth   = 2;
      ctx.stroke();

      // Outer ambient glow fading
      const glow = ctx.createRadialGradient(cx, cy, r * 0.75, cx, cy, r * 1.5);
      glow.addColorStop(0, `rgba(${matRgb}, ${ringOp * 0.22})`);
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.globalAlpha = ringOp;
      ctx.fillStyle   = glow;
      ctx.fillRect(cx - r * 1.6, cy - r * 1.6, r * 3.2, r * 3.2);
      ctx.restore();

    } else {
      // ── Done ───────────────────────────────────────────────────────────
      sc.remove();
      onComplete?.();
      return;
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}
